// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { Container, Row, Col, Card, CardHeader, CardBlock } from 'reactstrap';
import DateTimeField from 'react-datetime';

import moment from 'moment';

import { loadSurveys, deleteSurvey, editSurvey } from 'actions/surveys';
import type { Survey as SurveyType } from 'actions/surveys';
import { addImage, deleteImage } from 'actions/images';

import Hero from 'components/Hero';
import Community from 'components/Community';
import SurveyResult from 'components/SurveyResult';
import SurveyResultOpen from 'components/SurveyResult/SurveyResultOpen';
import EmergencyBreak from 'components/EmergencyBreak';

type Props = {
  // redux
  survey: ?SurveyType,
  onLoadSurveys: typeof loadSurveys,
  onDeleteSurvey: (reason: string) => void,
  onEditSurvey: (field: string, value: string) => void,
  onAddImage: (image: string) => void,
  onDeleteImage: () => void,
};

class SurveyView extends React.Component<Props> {
  componentWillMount() {
    const { onLoadSurveys } = this.props;
    onLoadSurveys();
  }

  _onDelete = async reason => {
    const { onLoadSurveys, onDeleteSurvey } = this.props;
    await onDeleteSurvey(reason);
    await onLoadSurveys();
  };

  _onAddImage = async image => {
    const { onAddImage, onLoadSurveys } = this.props;
    await onAddImage(image);
    await onLoadSurveys();
  };

  _onDeleteImage = async () => {
    const { onDeleteImage, onLoadSurveys } = this.props;
    await onDeleteImage();
    await onLoadSurveys();
  };

  _onFieldChange = async (field, value) => {
    const { onEditSurvey, onLoadSurveys } = this.props;
    await onEditSurvey(field, value);
    await onLoadSurveys();
  };

  render() {
    const { survey } = this.props;
    if (!survey) {
      return null;
    }
    const {
      title,
      description,
      createdAt,
      createdBy,
      endsAt,
      visibleIn,
      deleted,
      image,
      type,
    } = survey;

    const answersLookup = survey.answers.reduce((acc, { answerId, text }) => {
      acc[answerId] = text;
      return acc;
    }, {});
    const results = survey.votes.reduce((acc, { selectedAnswerId }) => {
      const a = answersLookup[selectedAnswerId];
      acc[a] = acc[a] || 0;
      acc[a]++;
      return acc;
    }, {});
    const votes = survey.answers.reduce((acc, { text }) => {
      acc[text] = results[text] || 0;
      return acc;
    }, {});

    const communityVotes = Object.keys(visibleIn).map(communityId => {
      const results = survey.votes
        .filter(v => v.communityId === communityId)
        .reduce((acc, { selectedAnswerId }) => {
          const a = answersLookup[selectedAnswerId];
          acc[a] = acc[a] || 0;
          acc[a]++;
          return acc;
        }, {});
      const votes = survey.answers.reduce((acc, { text }) => {
        acc[text] = results[text] || 0;
        return acc;
      }, {});
      const responses = survey.responses
        .filter(r => r.communityId === communityId)
        .map(r => r.text);

      return {
        title: visibleIn[communityId],
        votes,
        responses,
      };
    });

    return (
      <Container fluid>
        <Row>
          <Col>
            <Hero
              title={title}
              description={description}
              images={!!image ? [image] : []}
              createdAt={createdAt}
              createdBy={{
                ...createdBy,
                isCreatedByManager: true,
              }}
              onImageAdded={this._onAddImage}
              onImageDeleted={this._onDeleteImage}
              onFieldChanged={this._onFieldChange}
              maxImages={1}
            />
            {type === 'closed' && <SurveyResult title="Gesamt" votes={votes} />}
            {type === 'closed' &&
              communityVotes.map(({ title, votes }) => (
                <SurveyResult key={title} title={title} votes={votes} />
              ))}
            {type === 'open' &&
              communityVotes.map(({ title, responses }) => (
                <SurveyResultOpen
                  key={title}
                  title={title}
                  responses={responses}
                />
              ))}
          </Col>
          <Col xs="12" sm="12" md="12" lg="5" xl="5">
            <Community communities={Object.values(visibleIn)} />
            {!deleted && (
              <Card style={styles.endsAt}>
                <CardHeader style={styles.endsAtTitle}>
                  Umfrage {endsAt > +new Date() ? 'läuft' : 'lief'} bis zum
                </CardHeader>
                <CardBlock style={styles.endsAtContent}>
                  <DateTimeField
                    defaultValue={endsAt}
                    renderInput={(props, openCalendar) => {
                      return (
                        <span
                          onClick={openCalendar}
                          style={{ cursor: 'pointer' }}
                        >
                          {moment(endsAt).format('DD. MMMM YYYY - HH:mm')}
                        </span>
                      );
                    }}
                    onBlur={moment => {
                      this._onFieldChange('endsAt', moment.unix() * 1000);
                    }}
                  />
                </CardBlock>
              </Card>
            )}
            {!deleted && (
              <EmergencyBreak type="Umfrage" onDelete={this._onDelete} />
            )}
          </Col>
        </Row>
      </Container>
    );
  }
}

const styles = {
  endsAt: {
    width: '100%',
  },
  endsAtTitle: {
    fontWeight: 'bold',
  },
  endsAtContent: {},
};

const mapStateToProps = ({ surveys: { all: surveys } }, props) => {
  const { surveyId } = props.match.params;
  return {
    survey: surveys.find(s => s.surveyId === surveyId),
  };
};
const mapDispatchToProps = (dispatch, props) => {
  const { surveyId } = props.match.params;
  return {
    onLoadSurveys: (...args) => dispatch(loadSurveys(...args)),
    onDeleteSurvey: reason => dispatch(deleteSurvey(surveyId, reason)),
    onEditSurvey: (field, value) =>
      dispatch(editSurvey(surveyId, field, value)),
    onAddImage: image => dispatch(addImage('survey', surveyId, image)),
    onDeleteImage: () => dispatch(deleteImage('survey', surveyId, 0)),
  };
};

const SurveyViewContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(SurveyView);
export default SurveyViewContainer;



// WEBPACK FOOTER //
// ./src/views/Survey/Survey.js